import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { updateUser, getUserById } from "./_request";
import passeye from '../../assets/icons/passeye.svg';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';

const EditUser = ({ user: initialUser }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "user",
    permissions: []
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { authToken } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const roles = ["Admin", "User"];
  const permissionsList = [
    "Dashboard",
    "Home",
    "UserManagement",
    "Taaruf",
    "AboutSections",
    "FAQManagement",
    "Tazakraat",
    "Exhibition",
    "ExhibitionContent",
    "Munasibaat",
    "Tableghaat",
    "Noor-Diary",
    "Mujjalah",
    "Books",
    "Review-Tazakuraat",
    "ThemeManagement",
    "ClassForms",
    "namazTimings",
    "PostUpload",
    "PostApproval"
  ];

  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getUserById(authToken, id);
        if (response.Result) {
          const userData = response?.data[0];
          const newFormData = {
            email: userData?.email || "",
            password: "",
            role: userData?.role || "user",
            permissions: userData?.permissions || []
          };
          setFormData(newFormData);
          setInitialFormData(newFormData); // Store initial data for reset
        } else {
          setError("Failed to fetch user data.");
        }
      } catch (error) {
        console.error("Error fetching user:", error);
        setError("Error fetching user data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    // Use initialUser if provided, otherwise fetch
    if (initialUser && initialUser.id === id) {
      setFormData({
        email: initialUser.email || "",
        password: "",
        role: initialUser.role || "user",
        permissions: initialUser.permissions || []
      });
      setInitialFormData({
        email: initialUser.email || "",
        password: "",
        role: initialUser.role || "user",
        permissions: initialUser.permissions || []
      });
      setLoading(false);
    } else {
      fetchUser();
    }
  }, [authToken, id, initialUser]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePermissionChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      permissions: Array.isArray(prevState.permissions)
        ? checked
          ? [...prevState.permissions, value]
          : prevState.permissions.filter((perm) => perm !== value)
        : checked
        ? [value]
        : []
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = { ...formData };
      if (!payload.password) delete payload.password; // Exclude password if unchanged
      const response = await updateUser(authToken, id, payload);
      if (response.Result) {
        alert("User updated successfully!");
        navigate('/users');
      } else {
        setError("Failed to update user.");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Error updating user. Please try again.");
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData); // Reset to initial user data
    setError(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600 border-solid"></div>
      </div>
    );
  }

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate('/users')}
        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center mb-3"
      >
        Go Back
      </Button>
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">Edit User</h2>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Password (leave blank to keep unchanged)</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 border rounded pr-10"
                placeholder="Enter new password (optional)"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-2 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                <img src={passeye} alt="Toggle Password Visibility" className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium">Role</label>
            <Select
              name="role"
              value={formData.role}
              onChange={handleChange}
              fullWidth
              required
            >
              <MenuItem value="">Select...</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role} value={role.toLowerCase()}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium">Permissions</label>
            <div className="grid grid-cols-2 gap-2">
              {permissionsList.map((perm) => (
                <label key={perm} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    value={perm}
                    checked={formData.permissions.includes(perm)}
                    onChange={handlePermissionChange}
                  />
                  <span>{perm}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
            >
              Update User
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUser;